import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import type { NotificationHistoryListItem } from "src/types/broadcasts";
import {
  deleteBroadcastRecord,
  fetchBroadcastHistory,
} from "src/lib/broadcastService";
import type { ActivityActor } from "src/lib/activityService";
import { Constants } from "src/constant";
import BroadcastItem from "./BroadcastItem";

interface Props {
  canManage: boolean;
  actor: ActivityActor | null;
  onDuplicate: (item: NotificationHistoryListItem) => void;
  /** Only fired for draft rows — opens the composer with the draft's id. */
  onEdit?: (item: NotificationHistoryListItem) => void;
  refreshKey?: number;
}

export const BroadcastHistory: React.FC<Props> = ({
  canManage,
  actor,
  onDuplicate,
  onEdit,
  refreshKey = 0,
}) => {
  const [items, setItems] = useState<NotificationHistoryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<NotificationHistoryListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteBroadcastRecord(target, actor);
      setItems((prev) => prev.filter((i) => i.id !== target.id));
      if (expandedId === target.id) setExpandedId(null);
      setPendingDelete(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);

      try {
        const page = await fetchBroadcastHistory(null);
        setItems(page.items);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, [refreshKey]);

  const renderedItems = React.useMemo(() => {
    // Keeping your temporary duplicated list behavior from the pasted code.
    // Replace this with `items` when you no longer need test overflow.
    const list: NotificationHistoryListItem[] = [];

    for (let i = 0; i < items.length; i += 1) {
      list.push(items[i % items.length]);
    }

    return list;
  }, [items]);

  return (
    <section
      className={[
        "flex min-h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)] min-w-0 flex-col overflow-hidden",
        "rounded-[1.5rem] border-4 border-[#E5E5E5] bg-white",
        "shadow-[0_8px_0_#D0D0D0]",
      ].join(" ")}
    >
      {/* Pinned header */}
      <div className="shrink-0 border-b-2 border-[#E5E5E5] bg-white px-4 py-4 sm:px-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-extrabold text-[#1F1F1F] sm:text-lg">
            Past Broadcasts
          </h2>

          <p className="shrink-0 rounded-full bg-[#F7F7F7] px-2.5 py-1 text-xs font-bold text-[#777777]">
            Last {Constants.BROADCASTS_HISTORY_RETENTION_DAYS} days
          </p>
        </div>

        <p className="mt-1 text-xs text-[#777777]">
          Broadcasts are retained for{" "}
          {Constants.BROADCASTS_HISTORY_RETENTION_DAYS} days.
        </p>
      </div>

      {/* Scrollable body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-5">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[#777777]">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-2xl border-2 border-dashed border-[#E5E5E5] px-6 py-10 text-center text-sm text-[#777777]">
            No broadcasts sent yet.
          </p>
        ) : (
          <>
            {renderedItems.map((item, index) => (
              <BroadcastItem
                key={`${item.id}-${index}`}
                item={item}
                canManage={canManage}
                isExpanded={expandedId === item.id}
                onToggle={() =>
                  setExpandedId(expandedId === item.id ? null : item.id)
                }
                onDuplicate={onDuplicate}
                onEdit={onEdit}
                onDelete={(itemToDelete) => setPendingDelete(itemToDelete)}
              />
            ))}
          </>
        )}
      </div>

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-broadcast-title"
          onClick={(e) => {
            // Click outside dialog cancels (unless mid-delete).
            if (e.target === e.currentTarget && !deleting) {
              setPendingDelete(null);
              setDeleteError(null);
            }
          }}
        >
          <div className="w-full max-w-md rounded-[1.5rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-6">
            <h2
              id="delete-broadcast-title"
              className="mb-2 flex items-center gap-2 text-lg font-extrabold text-[#1F1F1F]"
            >
              <Trash2 className="h-5 w-5 text-rose-600" aria-hidden />
              Delete this entry?
            </h2>
            <p className="mb-4 text-sm text-[#6B7280]">
              Removes{" "}
              <span className="font-bold text-[#1F1F1F]">
                &ldquo;{pendingDelete.title}&rdquo;
              </span>{" "}
              {pendingDelete.status === "draft"
                ? Constants.BROADCASTS_DRAFT_DELETE_DESCRIPTION
                : Constants.BROADCASTS_DELETE_ENTRY_DESCRIPTION}
            </p>
            {deleteError && (
              <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                {deleteError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPendingDelete(null);
                  setDeleteError(null);
                }}
                disabled={deleting}
                className="rounded-xl border-2 border-[#E5E5E5] bg-white px-4 py-2 text-sm font-extrabold text-[#3C3C3C] shadow-[0_2px_0_#D0D0D0] transition active:translate-y-0.5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmDelete()}
                disabled={deleting}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-rose-600 bg-rose-500 px-4 py-2 text-sm font-extrabold uppercase tracking-wide text-white shadow-[0_3px_0_#9F1239] transition active:translate-y-0.5 disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Trash2 className="h-4 w-4" aria-hidden />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BroadcastHistory;
